export type Role =
  | "super_admin"
  | "admin"
  | "collection_agent"
  | "accountant"
  | "viewer";

export type { BadgeColor, BadgeVariant } from "../components/base/Badge";

export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: Role;
  avatarColor?: string;
}

export interface AuthSession {
  user: User;
  token: string;
}

/** Normalized error returned by the API service layer. */
export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(message: string, status = 500, code = "UNKNOWN_ERROR", details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Generic success payload shape. */
export interface ApiResponse<T> {
  data: T;
}

/** Generic paginated payload shape. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/* ================= Member Master (Phase 1) ================= */

export type MemberStatus = "active" | "inactive" | "blacklisted";

export type ProofType = "aadhaar" | "pan" | "voter" | "driving" | "passport";

export type DocumentType = ProofType | "photo" | "address" | "other";

export type EnrollmentStatus = "active" | "completed" | "blacklisted";

/** A member's participation inside a single kameti group. */
export interface MemberGroupEnrollment {
  groupId: string;
  groupName: string;
  ticketNumber: string;
  monthlyInstallment: number;
  joinedOn: string;
  /** Cycle number in which this member already won the pool, if any. */
  wonCycle?: number | null;
  wonOn?: string | null;
  outstanding: number;
  status: EnrollmentStatus;
}

export interface MemberDocument {
  id: string;
  docType: DocumentType;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedOn: string;
  url?: string;
}

export interface MemberGuarantor {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  address: string;
  linkedMemberId?: string | null;
}

/**
 * A member is global to the business, not tied to a single group.
 * Their identity is shared; their enrollments grow over time.
 */
export interface Member {
  id: string;
  memberCode: string;
  fullName: string;
  guardianName: string;
  primaryPhone: string;
  alternatePhone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pinCode: string;
  proofType: ProofType | "";
  proofNumber: string;
  bankAccountName: string;
  bankAccountNumber: string;
  ifsc: string;
  upiId: string;
  isOrganizer: boolean;
  accountBalance?: number;
  status: MemberStatus;
  notes: string;
  enrollments: MemberGroupEnrollment[];
  documents: MemberDocument[];
  guarantors: MemberGuarantor[];
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/** A chit group reference used for filtering and display (full group mgmt is Phase 2). */
export interface MemberGroupSummary {
  id: string;
  name: string;
  monthlyAmount: number;
}

export interface MemberQuery {
  search?: string;
  status?: string;
  hasDues?: boolean;
  groupId?: string;
}

/** A member form draft (everything except system-managed fields). */
export type MemberDraft = Omit<
  Member,
  "id" | "memberCode" | "enrollments" | "documents" | "guarantors" | "createdBy" | "createdAt" | "updatedBy" | "updatedAt"
>;

/* ================= Group Management (Phase 2) ================= */

export type GroupStatus = "draft" | "active" | "completed" | "cancelled";

export type CommissionType = "none" | "pool_percentage" | "discount_percentage" | "fixed";

export type DividendTiming = "same_month" | "next_month";

export type GroupCycleStatus = "pending" | "completed" | "cancelled";

/** A single enrolled member holding a numbered ticket in a group. */
export interface GroupTicket {
  ticketNumber: string;
  memberId: string;
}

/** A cycle that has already been decided (auction or reserved organizer cycle). */
export interface GroupWonCycle {
  cycle: number;
  memberId: string;
  /** The actual amount paid out to the winner this cycle. */
  winningBid: number;
}

/** A generated cycle row used by the group detail Cycles tab. */
export interface GroupCycleRow {
  cycle: number;
  periodLabel: string;
  isOrganizerReserved: boolean;
  winnerMemberId?: string | null;
  winningBid: number;
  discount: number;
  perMemberDividend: number;
  commissionPerMember?: number;
  commissionAmount?: number;
  netInstallment: number;
  status: GroupCycleStatus;
  totalDemanded?: number;
  totalCollected?: number;
  collectionStatus?: "pending" | "partial" | "paid" | "overdue";
  collectedPct?: number;
  payoutStatus?: PayoutStatus | "pending";
}

/**
 * A kameti group. The number of monthly cycles always equals the member count
 * and the chit value always equals installment * member count — both are
 * arithmetic and become permanently locked once the group activates.
 */
export interface KametiGroup {
  id: string;
  groupCode: string;
  name: string;
  monthlyInstallment: number;
  memberCount: number;
  chitValue: number;
  startDate: string;
  auctionDay: number;
  dueDay: number;
  organizerMemberId: string;
  reservedCycle: number;
  commissionType: CommissionType;
  commissionValue: number;
  minDiscountPct?: number | null;
  maxDiscountPct?: number | null;
  bidStep: number;
  blockOverdueBidding: boolean;
  dividendTiming: DividendTiming;
  gracePeriodDays: number;
  lateFee: number;
  lateFeeIsPercent: boolean;
  lateFeePerDay: boolean;
  status: GroupStatus;
  tickets: GroupTicket[];
  /** Cycles already decided (winner + amount paid). */
  wonCycles: GroupWonCycle[];
  cancelledReason?: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

export interface GroupQuery {
  search?: string;
  status?: GroupStatus;
}

/** Wizard payload — everything the organizer can configure before activation. */
export interface GroupDraft {
  name: string;
  monthlyInstallment: number;
  memberCount: number;
  startDate: string;
  auctionDay: number;
  dueDay: number;
  organizerMemberId: string;
  reservedCycle: number;
  commissionType: CommissionType;
  commissionValue: number;
  minDiscountPct?: number | null;
  maxDiscountPct?: number | null;
  bidStep: number;
  blockOverdueBidding: boolean;
  dividendTiming: DividendTiming;
  gracePeriodDays: number;
  lateFee: number;
  lateFeeIsPercent: boolean;
  lateFeePerDay: boolean;
}

/** A ticket enriched with live member details for display. */
/** A staff note on a ticket holder. The backend keeps the newest 3 and deletes them after 90 days. */
export interface MemberRemark {
  id: string;
  remark: string;
  followUpDate: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface GroupTicketView {
  seat: number;
  ticketNumber: string;
  memberId: string;
  groupMemberId?: string;
  enrollmentStatus?: string;
  member?: {
    id: string;
    memberCode: string;
    fullName: string;
    primaryPhone: string;
    city: string;
    status: MemberStatus;
  } | null;
  isOrganizer: boolean;
  wonCycle?: number | null;
  outstanding: number;
  otherGroups: { groupId: string; groupName: string; outstanding: number }[];
  remarks?: MemberRemark[];
}

export interface ReplacedMemberView {
  groupMemberId: string;
  memberId: string;
  memberCode: string;
  fullName: string;
  phone: string;
  ticketNo: number;
  joinedOn: string;
  exitedOn?: string | null;
  replacedBy?: string | null;
  remarks?: string | null;
  outstanding: number;
}

export interface ReplacementPreview {
  groupMemberId: string;
  groupId: string;
  ticketNo: number;
  member: {
    id: string;
    memberCode: string;
    fullName: string;
    phone: string;
  };
  totalPaid: number;
  pendingDues: number;
  installmentsCount: number;
  suggestedTakeoverAmount: number;
}

export interface ReplaceMemberPayload {
  newMemberId: string;
  paymentMode: "CASH" | "UPI" | "BANK_TRANSFER" | "CHEQUE";
  takeoverAmount: number;
  clearArrears?: boolean;
  refundAmount?: number;
  referenceNo?: string;
  remarks?: string;
}

/** A group plus its fully-resolved tickets and generated cycles. */
export interface GroupView extends KametiGroup {
  tickets: GroupTicketView[];
  cycles: GroupCycleRow[];
  completedCycles: number;
  totalDiscount: number;
  nextAuctionDate?: string | null;
  replacedMembers?: ReplacedMemberView[];
}

/* ================= Auction Console (Phase 3) ================= */

export type AuctionStatus = "pending" | "closed";

export type AuctionBidStatus = "standing" | "withdrawn";

export type AuctionEligibilityReason = "won" | "inactive" | "overdue" | "reserved" | "blacklisted" | "organizer";

/** A single bid placed during an auction. Entered by staff on behalf of members. */
export interface AuctionBid {
  id: string;
  memberId: string;
  /** The amount this member is willing to RECEIVE (not the discount). */
  amount: number;
  placedAt: string;
  placedBy: string;
  status: AuctionBidStatus;
}

/** A member who may bid in the current cycle. */
export interface AuctionEligibleMember {
  memberId: string;
  memberCode: string;
  fullName: string;
  primaryPhone: string;
  ticketNumber: string;
  /** Outstanding balance in this group (used for payout adjustment on close). */
  outstanding: number;
  /** The enrollment record id a real bid must reference — undefined in mock mode, where memberId alone is used throughout. */
  groupMemberId?: string;
}

/** A member excluded from bidding, always with a stated reason. */
export interface AuctionIneligibleMember {
  memberId: string;
  memberCode: string;
  fullName: string;
  primaryPhone: string;
  ticketNumber: string;
  reason: string;
  reasonType: AuctionEligibilityReason;
  detail?: string;
}

/** The authoritative outcome of a closed cycle, computed on close by the backend. */
export interface AuctionResult {
  winnerMemberId: string;
  /** The amount the winner will receive. */
  winningBid: number;
  discount: number;
  commission?: number;
  commissionPerMember?: number;
  perMemberDividend: number;
  /** Fractional remainder left after flooring the per-member dividend. */
  dividendRemainder: number;
  netInstallment: number;
  totalCollected: number;
  /** Any pending dues of the winner adjusted against their payout. */
  winnerPendingDues: number;
  winnerPayout: number;
  closedAt: string;
  closedBy: string;
}

/**
 * A fully-resolved auction view built by the service layer. Combines the raw
 * seed (which group + cycle + dates + bids) with the group's configuration and
 * member data to compute the pool, eligibility and current standing.
 */
export interface Auction {
  id: string;
  groupId: string;
  groupName: string;
  groupCode: string;
  cycle: number;
  periodLabel: string;
  scheduledDate: string;
  pool: number;
  monthlyInstallment: number;
  memberCount: number;
  isOrganizerReserved: boolean;
  organizer: { memberId: string; fullName: string; ticketNumber: string } | null;
  bidStep: number;
  minDiscountPct?: number | null;
  maxDiscountPct?: number | null;
  blockOverdueBidding: boolean;
  dividendTiming: DividendTiming;
  commissionType?: CommissionType;
  commissionValue?: number;
  status: AuctionStatus;
  eligible: AuctionEligibleMember[];
  ineligible: AuctionIneligibleMember[];
  /** Complete bid history — standing and withdrawn. */
  bids: AuctionBid[];
  result?: AuctionResult | null;
  /** True when an earlier cycle is still unresolved, so this auction cannot be opened. */
  blockedOutOfOrder?: boolean;
}

/** Raw auction seed stored in mocks / the service store (no derived fields). */
export interface AuctionSeed {
  id: string;
  groupId: string;
  cycle: number;
  scheduledDate: string;
  bids?: AuctionBid[];
  result?: AuctionResult | null;
  status?: AuctionStatus;
  closedAt?: string;
  closedBy?: string;
}

/* ================= Collection (Phase 4) ================= */

export type PaymentMode = "cash" | "upi" | "bank_transfer" | "cheque" | "member_account";

export type AccountTransactionType = "DEPOSIT" | "WITHDRAWAL" | "INSTALLMENT_DEDUCTION" | "PAYOUT_CREDIT" | "REFUND";

export interface MemberAccountTransaction {
  id: string;
  memberId: string;
  type: AccountTransactionType;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  mode: PaymentMode;
  referenceNo?: string | null;
  receiptNo?: string | null;
  paymentId?: string | null;
  payoutId?: string | null;
  installmentId?: string | null;
  remarks?: string | null;
  createdBy?: string | null;
  creatorName?: string | null;
  createdAt: string;
}

export interface AdvanceDepositInput {
  amount: number;
  mode: PaymentMode;
  referenceNo?: string;
  receivedOn?: string;
  remarks?: string;
}

export interface AdvanceWithdrawInput {
  amount: number;
  mode: PaymentMode;
  referenceNo?: string;
  withdrawnOn?: string;
  remarks?: string;
}

export type DemandStatus = "pending" | "partial" | "paid";

export interface PreviousDueItem {
  installmentId: string;
  cycleId: string;
  cycleNo: number;
  periodLabel: string;
  baseAmount: string | number;
  lateFee: string | number;
  netPayable: string | number;
  amountPaid: string | number;
  balance: string | number;
  dueDate: string;
  status: string;
}

/**
 * A single monthly installment demand for one member in one group/cycle.
 * Demands are derived from closed cycles (generated when the auction is decided),
 * never created here. Paid state is aggregated from receipts.
 */
export interface CollectionDemand {
  id: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  ticketNumber: string;
  baseInstallment: number;
  /** Per-member dividend credit that reduces this month's installment. */
  dividendCredit: number;
  /** Per-member commission charge for running the chit cycle. */
  commission: number;
  /** Any late fee applied on top of the net installment. */
  lateFee: number;
  netPayable: number;
  paid: number;
  balance: number;
  status: DemandStatus;
  dueDate: string;
  daysOverdue: number;
  /** The fee that would apply under this group's policy, if overdue. */
  applicableLateFee: number;
  hasLateFeeApplied: boolean;
  /** True when this member won the pool in this cycle. */
  wonThisCycle: boolean;
  /** The enrollment record a real payment must reference — undefined in mock mode, where memberId alone is used throughout. */
  groupMemberId?: string;
  /** Last payment receipt ID and number for quick receipt printing. */
  lastReceiptId?: string | null;
  lastReceiptNo?: string | null;
  accountBalance?: number;
  /** Previous pending cycle dues for this member in earlier cycles of this group */
  previousPendingDues?: PreviousDueItem[];
  totalPreviousPendingDues?: number;
  /** Active payments allocated to this installment (used for deletion/reversal). */
  payments?: InstallmentPaymentItem[];
}

export interface InstallmentPaymentItem {
  paymentId: string;
  receiptNo: string;
  amount: number | string;
  mode: PaymentMode;
  paidAt?: string;
  createdAt?: string;
}

/** One demand payment inside a receipt. */
export interface DemandPayment {
  demandId: string;
  amount: number;
  /** Carried alongside demandId so recordPayment can group a cross-group selection into one receipt per group — see CollectionDemand.groupMemberId. */
  groupMemberId?: string;
}

/** A receipt — the only way money is recorded. Never deleted, only reversed. */
export interface CollectionReceipt {
  id: string;
  receiptNumber: string;
  memberId: string;
  payments: DemandPayment[];
  total: number;
  mode: PaymentMode;
  reference: string;
  receivedOn: string;
  remarks: string;
  collectedBy: string;
  createdAt: string;
  reversedAt?: string | null;
  reversedBy?: string | null;
  reversalReason?: string | null;
}

/** A receipt enriched with member details and demand lines for display/print. */
export interface ReceiptView extends CollectionReceipt {
  memberName: string;
  memberCode: string;
  memberPhone: string;
  fatherOrSpouse?: string | null;
  startDate?: string;
  chitValue?: number;
  groupInstallmentAmount?: number;
  totalMembers?: number;
  groupCode?: string;
  groupName?: string;
  collectedByRole?: string | null;
  ticketNo?: number;
  lines: {
    groupName: string;
    groupCode: string;
    cycle: number;
    periodLabel: string;
    amount: number;
    baseAmount?: number;
    dividendCredit?: number;
    lateFee?: number;
    netPayable?: number;
    discountAmount?: number;
    dividendPerMember?: number;
    commissionAmount?: number;
    ticketNo?: number;
  }[];
  /** Server-generated in real-API mode (spec 14); falls back to a client-computed value in mock mode. */
  amountInWords?: string;
  /** The member's remaining balance across the demands in this receipt. */
  remainingBalance: number;
}

/** An audited late-fee application on a demand. */
export interface LateFeeApplication {
  id: string;
  demandId: string;
  amount: number;
  reason: string;
  appliedBy: string;
  appliedAt: string;
  waivedAt?: string | null;
  waivedBy?: string | null;
  waiveReason?: string | null;
}

/** Full collection view for one group + one cycle (the collection sheet). */
export interface CycleCollectionView {
  groupId: string;
  groupCode: string;
  groupName: string;
  monthlyInstallment: number;
  cycleId?: string;
  status?: string;
  cycle: number;
  periodLabel: string;
  isOrganizerReserved: boolean;
  /** True when this cycle's auction has not yet been held → no demands exist. */
  auctionPending: boolean;
  demandPerMember: number;
  commissionAmount?: number;
  commissionPerMember?: number;
  totalExpected: number;
  totalCollected: number;
  balance: number;
  demands: CollectionDemand[];
  availableCycles: { cycle: number; periodLabel: string }[];
}

/** One row on the collection dashboard — an active group's current collection. */
export interface GroupCollectionRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  demandPerMember: number;
  paidMembers: number;
  totalMembers: number;
  collected: number;
  expected: number;
  nextCycle: number;
  nextAuctionPending: boolean;
  nextAuctionDate: string | null;
  isComplete: boolean;
}

export interface CollectionDashboardData {
  todayCollection: number;
  monthCollection: number;
  totalOutstanding: number;
  overdueCount: number;
  groups: GroupCollectionRow[];
  todayReceipts: ReceiptView[];
}

/** Payload for recording a payment (one receipt, one or more demand payments). */
export interface RecordPaymentInput {
  memberId: string;
  payments: DemandPayment[];
  mode: PaymentMode;
  reference: string;
  receivedOn: string;
  remarks: string;
  actor: string;
  applyLateFee?: boolean | string[];
}

/* ================= Payout (Phase 5) ================= */

export type PayoutStatus = "pending_approval" | "approved" | "partially_disbursed" | "disbursed" | "reversed";

/** A manual deduction added on top of same-group dues, always with a reason. */
export interface PayoutManualDeduction {
  id: string;
  amount: number;
  reason: string;
  addedBy: string;
  addedAt: string;
}

/** A recorded handover of part (or all) of the net payable. */
export interface PayoutDisbursement {
  id: string;
  amount: number;
  mode: PaymentMode;
  reference: string;
  paidOn: string;
  remarks: string;
  disbursedBy: string;
  recordedAt: string;
}

/** Who approved the payout and when — a separate step from disbursement. */
export interface PayoutApproval {
  approvedBy: string;
  approvedAt: string;
  remarks: string;
}

/**
 * Raw payout override stored in the service store. Payouts originate when an
 * auction closes; this seed holds the recorded financial core (prize, same-group
 * dues, deductions) plus the approval / disbursement / reversal state. Funding
 * and winner details are derived from the group, member and collection data so
 * figures always agree with the other modules.
 */
export interface PayoutSeed {
  id: string;
  voucherNumber: string;
  groupId: string;
  cycle: number;
  createdAt: string;
  /** The prize amount (the winning bid) recorded when the cycle was decided. */
  prize: number;
  /** Same-group dues adjusted against the prize, itemised by cycle. */
  sameGroupDues: { cycle: number; amount: number }[];
  manualDeductions?: PayoutManualDeduction[];
  approval?: PayoutApproval | null;
  disbursements?: PayoutDisbursement[];
  reversedAt?: string | null;
  reversedBy?: string | null;
  reversalReason?: string | null;
}

/** One itemised same-group due, enriched with its period label. */
export interface PayoutDuesLine {
  cycle: number;
  periodLabel: string;
  amount: number;
}

/** A member's outstanding balance in another group — shown for awareness only, never deducted. */
export interface PayoutOtherGroupBalance {
  groupId: string;
  groupName: string;
  groupCode: string;
  outstanding: number;
}

/** A fully-resolved payout view built by the service layer. */
export interface PayoutView {
  id: string;
  voucherNumber: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  isOrganizerReserved: boolean;
  winnerMemberId: string;
  winnerCode: string;
  winnerName: string;
  winnerPhone: string;
  winnerCity: string;
  winnerTicketNumber: string;
  isOrganizer: boolean;
  bankAccountName: string;
  ifsc: string;
  pool: number;
  prize: number;
  commissionAmount?: number;
  discount: number;
  perMemberDividend: number;
  expectedCollection: number;
  collected: number;
  fundingShortfall: number;
  sameGroupDues: PayoutDuesLine[];
  sameGroupDuesTotal: number;
  otherGroupBalances: PayoutOtherGroupBalance[];
  manualDeductions: PayoutManualDeduction[];
  manualDeductionsTotal: number;
  netPayable: number;
  amountPaid: number;
  balanceOwed: number;
  status: PayoutStatus;
  approval?: PayoutApproval | null;
  disbursements: PayoutDisbursement[];
  reversedAt?: string | null;
  reversedBy?: string | null;
  reversalReason?: string | null;
  createdAt: string;
}

/** Payload for recording a disbursement against an approved payout. */
export interface RecordDisbursementInput {
  amount: number;
  mode: PaymentMode;
  reference: string;
  paidOn: string;
  remarks: string;
  actor: string;
}

/* ================= Ledger & Reports (Phase 6) ================= */

/** The six kinds of financial event that create a ledger entry. */
export type LedgerEntryType =
  | "installment_due"
  | "dividend_credit"
  | "payment_received"
  | "late_fee"
  | "auction_won"
  | "payout_disbursed"
  | "adjustment";

/**
 * A single auditable ledger entry for a member. The installment due is debited
 * at the FULL monthly installment and the dividend is credited separately (never
 * a reduced debit + separate dividend, which would count the benefit twice).
 */
export interface LedgerEntry {
  id: string;
  memberId: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number | null;
  periodLabel: string | null;
  /** When the entry took effect (due date, receipt date, payout date, etc.). */
  date: string;
  type: LedgerEntryType;
  narration: string;
  /** Money owed by the member (installment due, late fee, debit adjustment). */
  debit: number;
  /** Money benefiting the member (dividend, payment, credit adjustment). */
  credit: number;
  /** Running contribution balance AFTER this entry. Payout entries do not move it. */
  runningBalance: number;
  /** Source document: receipt no., voucher no., cycle, adjustment ref. */
  reference: string;
  /** True for reversed/waived records (kept for audit, excluded from balance). */
  reversed?: boolean;
}

export type LedgerAdjustmentKind = "debit" | "credit";

/** A seeded manual correction to a member's ledger, always with a reason. */
export interface LedgerAdjustmentSeed {
  id: string;
  memberId: string;
  groupId: string;
  cycle: number | null;
  date: string;
  kind: LedgerAdjustmentKind;
  amount: number;
  narration: string;
  reason: string;
  reference: string;
}

/** One row of the cycle-wise statement table for a member in a group. */
export interface StatementCycleRow {
  cycle: number;
  periodLabel: string;
  /** Full installment charged (before dividend). */
  baseInstallment: number;
  /** Dividend credited separately. */
  dividendCredit: number;
  commissionPerMember?: number;
  netPayable: number;
  paid: number;
  balance: number;
  accountBalance?: number;
  dueDate?: string;
  paymentDate: string | null;
  receiptNumber: string | null;
  won: boolean;
  isReserved: boolean;
}

/** A member's payout record as shown on their statement. */
export interface StatementPayoutRow {
  voucherNumber: string;
  cycle: number;
  periodLabel: string;
  prize: number;
  duesAdjusted: number;
  netReceived: number;
  paidOn: string | null;
  status: PayoutStatus;
}

/** The full group-wise statement for one member in one group. */
export interface MemberStatementGroup {
  groupId: string;
  groupCode: string;
  groupName: string;
  ticketNumber: string;
  monthlyInstallment: number;
  joinedOn: string;
  wonCycle: number | null;
  wonOn: string | null;
  cycles: StatementCycleRow[];
  payouts: StatementPayoutRow[];
  totalCharged: number;
  totalDividend: number;
  totalCommission?: number;
  totalNetPayable?: number;
  totalPaid: number;
  totalOutstanding: number;
  cyclesRemaining: number;
}

/** The group-wise member statement document. */
export interface MemberStatement {
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  city: string;
  generatedAt: string;
  periodLabel: string;
  group: MemberStatementGroup;
}

/** Per-group summary figures used in the consolidated statement. */
export interface ConsolidatedGroupSummary {
  groupId: string;
  groupCode: string;
  groupName: string;
  ticketNumber: string;
  monthlyInstallment: number;
  totalCharged: number;
  totalDividend: number;
  totalPaid: number;
  totalOutstanding: number;
  wonCycle: number | null;
  cyclesRemaining: number;
}

/** The consolidated statement — one member across all their groups. */
export interface MemberConsolidatedStatement {
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  city: string;
  generatedAt: string;
  groups: ConsolidatedGroupSummary[];
  totalOutstanding: number;
  totalDividend: number;
  payoutCount: number;
}

export type AgeingBucket = "not_due" | "b1_30" | "b31_60" | "b61_90" | "b90_plus";

/** One member on the outstanding report, grouped so overdue demands total once. */
export interface OutstandingRow {
  memberId: string;
  memberName: string;
  memberPhone: string;
  totalOutstanding: number;
  demands: CollectionDemand[];
  buckets: Record<AgeingBucket, number>;
}

export interface OutstandingReport {
  rows: OutstandingRow[];
  flat: CollectionDemand[];
  bucketTotals: Record<AgeingBucket, number>;
  grandTotal: number;
}

export interface AuctionHistoryRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  scheduledDate: string;
  winnerMemberId: string;
  winnerName: string;
  winnerTicket: string;
  pool: number;
  winningBid: number;
  discount: number;
  discountPct: number;
  perMemberDividend: number;
  isOrganizerReserved: boolean;
}

export interface AuctionHistoryData {
  rows: AuctionHistoryRow[];
  totalPool: number;
  totalWinningBids: number;
  totalDiscounts: number;
  avgDiscount: number;
  avgDiscountPct: number;
}

export interface GroupEarningRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  reservedCycle: number;
  reservedCyclePassed: boolean;
  reservedPool: number;
  commission: number;
  totalEarning: number;
}

export interface EarningsReport {
  groups: GroupEarningRow[];
  totalReserved: number;
  totalCommission: number;
  totalEarning: number;
}

/** Per-cycle performance within the group performance report. */
export interface GroupPerformanceCycle {
  cycle: number;
  periodLabel: string;
  winnerName: string;
  winningBid: number;
  discount: number;
  perMemberDividend: number;
  status: GroupCycleStatus;
  collected: number;
  expected: number;
  collectionPct: number;
}

/** Per-member standing within the group performance report. */
export interface GroupPerformanceMember {
  /** Absent in mock mode — remarks need a real enrollment id. */
  groupMemberId?: string;
  isActiveEnrollment?: boolean;
  remarks?: MemberRemark[];
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  ticketNumber: string;
  wonCycle: number | null;
  outstanding: number;
}

/** Just the group fields the performance report's header/export actually reads — not the full GroupView. */
export interface GroupPerformanceGroupSummary {
  groupId: string;
  groupCode: string;
  name: string;
  monthlyInstallment: number;
  memberCount: number;
  chitValue: number;
  startDate: string;
  auctionDay: number;
  reservedCycle: number;
  status: GroupStatus;
}

export interface GroupPerformanceReport {
  group: GroupPerformanceGroupSummary;
  cycles: GroupPerformanceCycle[];
  members: GroupPerformanceMember[];
  totalCollected: number;
  totalExpected: number;
  currentOutstanding: number;
}

/** One row of the collections-vs-payouts monthly trend. */
export interface MonthlyTrendPoint {
  month: string;
  collections: number;
  payouts: number;
}

/** Just the auction fields the dashboard's upcoming-auctions widget actually reads — not the full Auction. */
export interface DashboardUpcomingAuction {
  id: string;
  groupId: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  scheduledDate: string;
}

/** Just the receipt fields the dashboard's recent-collections widget actually reads — not the full ReceiptView. */
export interface DashboardRecentCollection {
  id: string;
  receiptNumber: string;
  memberName: string;
  total: number;
  receivedOn: string;
  reversedAt?: string | null;
  lines: { groupName: string }[];
}

/** Aggregated data for the live dashboard. */
export interface DashboardData {
  activeGroups: number;
  totalMembers: number;
  monthCollection: number;
  pendingOverdue: number;
  trend: MonthlyTrendPoint[];
  groupPerformance: { name: string; collected: number; expected: number; pct: number }[];
  upcomingAuctions: DashboardUpcomingAuction[];
  recentCollections: DashboardRecentCollection[];
  ageing: { bucket: AgeingBucket; label: string; amount: number }[];
}

/* ================= Settings, Users, Audit & Search (Phase 7) ================= */

/** The organizer's business identity, printed on every receipt/voucher/report. */
export interface BusinessSettings {
  businessName: string;
  tagline?: string;
  proprietor: string;
  address: string;
  city: string;
  state: string;
  pinCode: string;
  primaryPhone: string;
  alternatePhone: string;
  email: string;
  website: string;
  /** Registration / GST number shown on printed documents. */
  registrationNumber: string;
  /** Optional logo for printed documents. */
  logoUrl: string;
}

export interface PublicBranding {
  businessName: string;
  tagline: string;
  logoUrl: string;
}

export type NumberingKey = "member_code" | "group_code" | "receipt" | "payout_voucher";

/** A single numbering sequence (prefix + zero-padded running number). */
export interface NumberingRule {
  id: NumberingKey;
  title?: string;
  prefix: string;
  /** Zero-pad width, e.g. 4 → MEM0001. */
  padding: number;
  /** The next number that will be issued. Never moves backward. */
  next: number;
  /** Optionally reset the running number to 1 each financial year. */
  resetAnnually: boolean;
}

/** Document numbering configuration. */
export interface DocumentNumbering {
  rules: NumberingRule[];
}

/** Values pre-filled when a new group is created (Phase 2 wizard). */
export interface GroupDefaults {
  reservedCycle: number;
  commissionType: CommissionType;
  commissionValue: number;
  gracePeriodDays: number;
  lateFee: number;
  lateFeeIsPercent: boolean;
  lateFeePerDay: boolean;
  bidStep: number;
  blockOverdueBidding: boolean;
  dividendTiming: DividendTiming;
  auctionDay: number;
  dueDay: number;
}

/** Full application settings bundle. */
export interface AppSettings {
  business: BusinessSettings;
  numbering: DocumentNumbering;
  defaults: GroupDefaults;
  updatedBy: string;
  updatedAt: string;
}

export type UserStatus = "active" | "inactive";

/** A staff user. Distinct from a Member — members are customers, users are employees. */
export interface StaffUser extends User {
  status: UserStatus;
  lastLogin: string | null;
  createdAt: string;
}

export interface UserDraft {
  name: string;
  email: string;
  phone: string;
  role: Role;
  status: UserStatus;
  password?: string;
}

export type AuditActionType =
  | "create"
  | "update"
  | "delete"
  | "activate"
  | "enroll"
  | "remove"
  | "record_bid"
  | "withdraw_bid"
  | "close_auction"
  | "record_receipt"
  | "reverse_receipt"
  | "apply_late_fee"
  | "waive_late_fee"
  | "approve_payout"
  | "disburse_payout"
  | "reverse_payout"
  | "ledger_adjust"
  | "create_user"
  | "update_user"
  | "deactivate_user"
  | "reset_password"
  | "update_setting";

/** A field-level change captured inside an audit entry. */
export interface AuditChange {
  field: string;
  before: string;
  after: string;
}

/** One audited action. Read-only for every role; export permitted. */
export interface AuditEntry {
  id: string;
  timestamp: string;
  userName: string;
  userRole: Role;
  action: AuditActionType;
  module: string;
  /** Human-readable affected record, e.g. "Member #MEM0001". */
  entity: string;
  narration: string;
  changes: AuditChange[];
}

export interface AuditQuery {
  search?: string;
  action?: string;
  module?: string;
  user?: string;
  from?: string;
  to?: string;
}

export type NotificationCategory =
  | "auction_due"
  | "auction_overdue"
  | "collection_overdue"
  | "payout_approval"
  | "demand_overdue"
  | "device_approval";

/** An in-application notification, linked to the relevant screen. */
export interface NotificationItem {
  id: string;
  category: NotificationCategory;
  title: string;
  description: string;
  link: string;
  createdAt: string;
  read: boolean;
}

export type SearchResultType = "member" | "group" | "receipt" | "voucher";

/** A grouped global-search result with a navigation target. */
export interface SearchResult {
  type: SearchResultType;
  label: string;
  sublabel: string;
  link: string;
}

export type DeviceApprovalStatus = "PENDING" | "APPROVED" | "REJECTED" | "REVOKED";

export interface UserDevice {
  id: string;
  userId: string;
  userName?: string | null;
  userEmail?: string | null;
  userRole?: Role | null;
  deviceId?: string | null;
  deviceLabel?: string | null;
  browser?: string | null;
  os?: string | null;
  ipAddress?: string | null;
  status: DeviceApprovalStatus;
  requestedAt: string;
  approvedAt?: string | null;
  approvedBy?: string | null;
  approvedByName?: string | null;
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  rejectReason?: string | null;
  lastUsedAt?: string | null;
  createdAt?: string | null;
}